import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Play, Calendar, ArrowUpRight, ExternalLink, Search } from 'lucide-react';
import api, { resolveImageUrl } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';

const FeaturedReleases = () => {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Semua');
  const [q, setQ] = useState('');

  useEffect(() => {
    api
      .get('/releases', { params: { limit: 24 } })
      .then((res) => setReleases(res.data))
      .catch(() => setReleases([]))
      .finally(() => setLoading(false));
  }, []);

  const genres = useMemo(
    () => ['Semua', ...Array.from(new Set(releases.map((h) => h.genre)))],
    [releases]
  );

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return releases
      .filter((h) => {
        const matchG = filter === 'Semua' || h.genre === filter;
        const matchQ =
          !query ||
          h.title.toLowerCase().includes(query) ||
          h.artist.toLowerCase().includes(query);
        return matchG && matchQ;
      })
      .slice(0, 12);
  }, [releases, filter, q]);

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <section id="releases" className="relative py-24 lg:py-32 bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="inline-block px-4 py-1.5 rounded-full border border-[#D4A94F]/30 bg-[#D4A94F]/5 mb-5">
              <span className="text-xs font-medium tracking-widest uppercase text-[#E9C46A]">
                Highlight Rilisan
              </span>
            </div>
            <h2 className="text-white font-bold text-4xl md:text-5xl leading-tight tracking-tight">
              Karya Pilihan dari
              <br />
              <span className="gold-gradient-text">Artis Kami</span>
            </h2>
          </div>
          <Button
            asChild
            variant="outline"
            className="self-start bg-transparent border border-white/15 hover:border-[#D4A94F] hover:bg-[#D4A94F]/5 text-white rounded-full btn-transition"
          >
            <Link to="/rilisan">
              Lihat Semua <ArrowUpRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-10">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
            <Input
              placeholder="Cari judul atau artis..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-11 h-11 rounded-full bg-[#0f0f0f] border-white/10 focus-visible:ring-[#D4A94F] focus-visible:border-[#D4A94F] text-white"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {genres.map((g) => (
              <button
                key={g}
                onClick={() => setFilter(g)}
                className={`px-4 py-2 rounded-full text-xs font-semibold btn-transition border ${
                  filter === g
                    ? 'bg-[#D4A94F] text-black border-[#D4A94F]'
                    : 'bg-transparent text-neutral-300 border-white/10 hover:border-[#D4A94F]/50 hover:text-white'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="aspect-square rounded-2xl bg-[#111] animate-pulse border border-white/[0.04]"
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-neutral-500">
            Tidak ada rilisan yang cocok.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((r) => (
              <div
                key={r.id}
                className="group relative rounded-2xl overflow-hidden bg-[#111] border border-white/[0.06] hover:border-[#D4A94F]/40 card-transition"
              >
                <div className="aspect-square relative overflow-hidden bg-neutral-900">
                  {r.cover_image && (
                    <img
                      src={resolveImageUrl(r.cover_image)}
                      alt={r.title}
                      className="w-full h-full object-cover btn-transition group-hover:scale-105"
                      loading="lazy"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-70" />
                  <div className="absolute top-3 left-3">
                    <span className="text-[10px] tracking-widest uppercase font-bold text-black bg-[#D4A94F] px-2.5 py-1 rounded-full">
                      {r.genre}
                    </span>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 btn-transition">
                    <a
                      href={r.spotify_url || r.youtube_url || '#'}
                      target={r.spotify_url || r.youtube_url ? '_blank' : undefined}
                      rel="noreferrer"
                      className="w-14 h-14 rounded-full bg-[#D4A94F] flex items-center justify-center shadow-[0_10px_30px_-5px_rgba(212,169,79,0.6)] hover:scale-110 btn-transition"
                    >
                      {r.spotify_url || r.youtube_url ? (
                        <ExternalLink className="w-5 h-5 text-black" />
                      ) : (
                        <Play className="w-5 h-5 text-black fill-black ml-0.5" />
                      )}
                    </a>
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="text-white font-semibold text-base leading-tight truncate">
                    {r.title}
                  </h4>
                  <p className="text-neutral-400 text-sm truncate mt-0.5">{r.artist}</p>
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-neutral-500">
                    <Calendar className="w-3 h-3" />
                    <span>{fmtDate(r.release_date)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedReleases;
